import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { EmailConfigData, getEmailConfig, updateEmailConfig, toggleEmailConfigStatus } from '../../api/emailConfig';

const EmailConfig: React.FC = () => {
  const [config, setConfig] = useState<EmailConfigData>({
    senderName: '',
    senderEmail: '',
    brevoApiKey: '',
    active: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      setLoading(true);
      const data = await getEmailConfig();
      setConfig(data);
      setError(null);
    } catch (err) {
      setError('Failed to load email configuration');
      
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await updateEmailConfig(config);
      enqueueSnackbar('Email configuration updated successfully', { variant: 'success' });
      setError(null);
    } catch (err) {
      setError('Failed to update email configuration');
      
      enqueueSnackbar('Failed to update email configuration', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    if (name === 'active') {
      setConfig(prev => ({ ...prev, [name]: checked }));
    } else {
      setConfig(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleToggleStatus = async () => {
    try {
      setLoading(true);
      const updatedConfig = await toggleEmailConfigStatus();
      setConfig(updatedConfig);
      enqueueSnackbar(`Email service ${updatedConfig.active ? 'activated' : 'deactivated'} successfully`, { variant: 'success' });
    } catch (err) {
      setError('Failed to toggle email service status');
      
      enqueueSnackbar('Failed to toggle email service status', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: 2,
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      color: 'white',
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Email Configuration
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Sender Name"
            name="senderName"
            value={config.senderName}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            InputLabelProps={{
              sx: { color: 'rgba(255, 255, 255, 0.7)' }
            }}
            InputProps={{
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#60a5fa',
                },
                color: 'white',
              }
            }}
          />
          <TextField
            fullWidth
            label="Sender Email"
            name="senderEmail"
            type="email"
            value={config.senderEmail}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            InputLabelProps={{
              sx: { color: 'rgba(255, 255, 255, 0.7)' }
            }}
            InputProps={{
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#60a5fa',
                },
                color: 'white',
              }
            }}
          />
          <TextField
            fullWidth
            label="Brevo API Key"
            name="brevoApiKey"
            value={config.brevoApiKey}
            onChange={handleChange}
            margin="normal"
            required
            type="password"
            disabled={loading}
            InputLabelProps={{
              sx: { color: 'rgba(255, 255, 255, 0.7)' }
            }}
            InputProps={{
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#60a5fa',
                },
                color: 'white',
              }
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ 
              mt: 2,
              backgroundColor: 'rgba(59, 130, 246, 0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.9)',
              },
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </Button>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">
              Email Service Status: <strong>{config.active ? 'Active' : 'Inactive'}</strong>
            </Typography>
            <Button
              variant="outlined"
              onClick={handleToggleStatus}
              disabled={loading || !config.brevoApiKey}
              sx={{
                borderColor: config.active ? 'rgba(239, 68, 68, 0.6)' : 'rgba(16, 185, 129, 0.6)',
                color: config.active ? '#ef4444' : '#10b981',
                '&:hover': {
                  borderColor: config.active ? 'rgba(239, 68, 68, 0.8)' : 'rgba(16, 185, 129, 0.8)',
                  backgroundColor: config.active ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'
                },
                backdropFilter: 'blur(4px)',
              }}
            >
              {config.active ? 'Deactivate' : 'Activate'} Email Service
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailConfig;
